// 🔒 B360 (AI_156) — IDENTIFICADORES QUE NÃO SAEM PARA A IA
// ─────────────────────────────────────────────────────────────
// A rede B209 acusou NOME e RGCT da paciente nos dados enviados à IA:
//  • RGCT: ia de propósito (os laudos citam "RGCT nº …"). Agora vai à IA um
//    MARCADOR (§RGCT-<8 primeiros do hash>§) e o número real volta só no
//    navegador, na resposta (callAnalyze → reidentificarObjeto). O hash no
//    marcador impede trocar o RGCT de um paciente pelo de outro.
//  • NOME: chegava por TEXTO LIVRE (cartas/receitas/evoluções digitadas com o
//    nome). O nome completo da própria paciente (e primeiro + último nome) vira
//    "[paciente]", sem diferenciar maiúsculas/acentos. Só o nome EXATO dela —
//    não apaga termo clínico (por isso o filtro geral de PII não toca nomes).
// ─────────────────────────────────────────────────────────────

const rgctPorHash = new Map<string, string>();

export function marcadorRGCT(pacienteHash: string): string {
  return `§RGCT-${String(pacienteHash).slice(0, 8)}§`;
}

/** Guarda (só na memória deste navegador) o RGCT real do marcador. */
export function registrarRGCT(pacienteHash: string, rgct: string): string {
  const m = marcadorRGCT(pacienteHash);
  rgctPorHash.set(m, rgct);
  return m;
}

/** Troca os marcadores de RGCT pelo número real (desconhecido → texto neutro). */
export function reidentificarTexto(texto: string): string {
  if (typeof texto !== "string" || !texto.includes("§RGCT-")) return texto;
  return texto.replace(/§RGCT-[^§\s]{1,16}§/g, (m) => rgctPorHash.get(m) || "(RGCT registrado)");
}

export function reidentificarObjeto<T>(valor: T): T {
  if (typeof valor === "string") return reidentificarTexto(valor) as any;
  if (Array.isArray(valor)) return valor.map((v) => reidentificarObjeto(v)) as any;
  if (valor && typeof valor === "object") {
    const out: Record<string, any> = {};
    for (const k of Object.keys(valor as any)) out[k] = reidentificarObjeto((valor as any)[k]);
    return out as any;
  }
  return valor;
}

// Letra → classe que aceita a letra com ou sem acento, maiúscula ou minúscula.
const VARIANTES: Record<string, string> = {
  a: "aáàâãä", e: "eéèêë", i: "iíìîï", o: "oóòôõö", u: "uúùûü", c: "cç", n: "nñ",
};
function letraFlexivel(ch: string): string {
  const base = ch.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const v = VARIANTES[base];
  if (v) return `[${v}${v.toUpperCase()}]`;
  return ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function regexDoNome(partes: string[]): RegExp {
  const corpo = partes
    .map((p) => Array.from(p).map(letraFlexivel).join(""))
    .join("\\s+");
  return new RegExp(`(?<![\\p{L}])${corpo}(?![\\p{L}])`, "giu");
}

/**
 * B372 (AI_156) — NOME DA MÃE, que o detector B209 pegou chegando à IA.
 * Vem digitado em TEXTO LIVRE (evolução, carta, ficha), igual ao nome da
 * paciente e ao RGCT: o anonimizador monta objeto só com campos seguros, então
 * o cadastro não é a via. No Brasil o nome da mãe é usado como FATOR DE
 * IDENTIFICAÇÃO — é identificador forte, não detalhe.
 *
 * Diferença deliberada em relação ao nome da paciente: aqui só o nome
 * COMPLETO. Primeiro + último de um nome de mãe ("Maria Rosa", "Ana Flor")
 * tem risco real de apagar termo clínico; o nome completo não.
 */
export function padroesNomeMae(nomeMae?: string): RegExp[] {
  const partes = String(nomeMae || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length < 2) return [];
  return [regexDoNome(partes)];
}

/** Padrões do nome da paciente: completo e primeiro + último (se ambos ≥ 3 letras). */
export function padroesNomePaciente(nome?: string): RegExp[] {
  const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length < 2) return [];
  const padroes = [regexDoNome(partes)];
  const primeiro = partes[0];
  const ultimo = partes[partes.length - 1];
  if (partes.length > 2 && primeiro.length >= 3 && ultimo.length >= 3) {
    padroes.push(regexDoNome([primeiro, ultimo]));
  }
  return padroes;
}

/**
 * Troca, em todo texto livre, o NÚMERO do RGCT pelo marcador — o número é
 * digitado à mão em cartas à Central e evoluções, e escapava do campo
 * estruturado. Como vira o MESMO marcador, o navegador o devolve ao normal.
 */
export function substituirRGCTEmTexto<T>(valor: T, rgct: string, marcador: string): T {
  const alvo = String(rgct || "").trim();
  if (alvo.length < 3) return valor;
  const re = new RegExp(alvo.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  const aplicar = (v: any): any => {
    if (typeof v === "string") return v.replace(re, marcador);
    if (Array.isArray(v)) return v.map(aplicar);
    if (v && typeof v === "object") {
      const out: Record<string, any> = {};
      for (const k of Object.keys(v)) out[k] = aplicar(v[k]);
      return out;
    }
    return v;
  };
  return aplicar(valor);
}

// B365 (AI_156) — IDENTIFICADORES VINDOS DO CABEÇALHO DE UM LAUDO EM PDF.
// A análise de exame de imagem lê o PDF inteiro, e o PDF traz o cabeçalho do
// serviço com NOME e PRONTUÁRIO do paciente. Sem instrução em contrário, a IA
// transcrevia isso para o texto da análise, que é salvo no exame e depois entra
// no prompt da análise geral (achado ao vivo, 28/10/2026 — colangio-RM).
// Campos cujo VALOR é redigido quando aparece no texto. `nome` tem tratamento
// próprio (acento/caixa/primeiro+último). `nascimento` fica FORA de propósito:
// é uma data, e redigir datas arrisca apagar a data de um exame; a idade, que é
// o que importa clinicamente, não identifica.
const CAMPOS_IDENTIFICADORES = [
  "prontuario",
  "matricula",
  "hospub",
  "cns",
  "rg",
  "cpf",
  "rgct",
  "numeroRGCT",
  "passaporte",
];

/**
 * Redige, em texto gerado pela IA, o NOME do paciente e os valores dos seus
 * identificadores de cadastro. Valores com menos de 4 caracteres são ignorados
 * (colidiriam com número clínico). Casa com fronteira de palavra, então não
 * corta um número maior pela metade.
 */
export function redigirIdentificadoresDoPaciente<T>(
  valor: T,
  paciente?: Record<string, unknown> | null
): T {
  if (!paciente) return valor;
  const padroesNome = padroesNomePaciente(paciente.nome as string | undefined);
  const padroesId: RegExp[] = [];
  // B382-FU (AI_156): a DATA DE NASCIMENTO ficava fora de propósito — redigir
  // datas genericamente arriscaria apagar a data de um exame. Mas aqui não é
  // padrão genérico: é o VALOR exato do cadastro do paciente, nos dois formatos. O modelo
  // transcreveu "(01/07/1965)" do cabeçalho do PDF da biópsia apesar da regra
  // de prompt nomeá-la (01/11/2026) — prompt é pedido, isto é garantia. O risco
  // residual (exame feito exatamente no aniversário de nascimento) é desprezível
  // neste tipo de prontuário, e esta função só atua na SAÍDA da IA.
  for (const campo of ["dataNascimento", "nascimento"]) {
    const bruto = paciente[campo];
    const iso = String(bruto ?? "").trim();
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) continue;
    const [, ano, mes, dia] = m;
    for (const forma of [`${dia}/${mes}/${ano}`, `${ano}-${mes}-${dia}`]) {
      padroesId.push(
        new RegExp(`(?<![\\d])${forma.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\d])`, "g")
      );
    }
  }
  for (const campo of CAMPOS_IDENTIFICADORES) {
    const bruto = paciente[campo];
    const v = typeof bruto === "string" || typeof bruto === "number" ? String(bruto).trim() : "";
    if (v.length >= 4) {
      padroesId.push(
        new RegExp(`(?<![\\p{L}\\d])${v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\p{L}\\d])`, "giu")
      );
    }
  }
  if (padroesNome.length === 0 && padroesId.length === 0) return valor;

  const aplicar = (v: any): any => {
    if (typeof v === "string") {
      let out = padroesNome.reduce((t, re) => t.replace(re, "[paciente]"), v);
      out = padroesId.reduce((t, re) => t.replace(re, "[identificador removido]"), out);
      return out;
    }
    if (Array.isArray(v)) return v.map(aplicar);
    if (v && typeof v === "object") {
      const o: Record<string, any> = {};
      for (const k of Object.keys(v)) o[k] = aplicar(v[k]);
      return o;
    }
    return v;
  };
  return aplicar(valor);
}

export function redigirNomeEmObjeto<T>(
  valor: T,
  padroes: RegExp[],
  // B372: quem é redigido muda o rótulo — "[paciente]" × "[mãe]". O rótulo
  // importa: a IA deve saber que havia ali um nome, e de quem, sem o nome.
  rotulo = "[paciente]"
): T {
  if (padroes.length === 0) return valor;
  if (typeof valor === "string") {
    return padroes.reduce((s, re) => s.replace(re, rotulo), valor as string) as any;
  }
  if (Array.isArray(valor))
    return valor.map((v) => redigirNomeEmObjeto(v, padroes, rotulo)) as any;
  if (valor && typeof valor === "object") {
    const out: Record<string, any> = {};
    for (const k of Object.keys(valor as any))
      out[k] = redigirNomeEmObjeto((valor as any)[k], padroes, rotulo);
    return out as any;
  }
  return valor;
}
